import { KeyboardArrowDownRounded, RestartAltRounded } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAsyncAction, useRandomPassword, useStateRef } from 'app/services';
import { userTypeFormLoad } from 'app/store/user';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

const UserForm = forwardRef((props, ref) => {
  const theme = useTheme();
  // eslint-disable-next-line no-unused-vars
  const chars = [...'0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'];
  const { session } = useSelector((state) => state.session);
  const { typeItems } = useSelector((state) => state.user);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);

  const { asyncAction: fetchTypeData, isLoading: isFetchTypeLoading } = useAsyncAction(userTypeFormLoad);
  const { password, generateRandomPassword } = useRandomPassword();
  const { defaultValues, onSubmit } = props;
  const { control, reset, register, setValue, formState, watch, handleSubmit } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      role_id: defaultValues ? defaultValues.role_id : '',
      email: defaultValues ? defaultValues.email : '',
      phone: defaultValues ? defaultValues.phone : '',
      can_close_all_notification: defaultValues ? defaultValues.can_close_all_notification : false,
      show_notification: defaultValues ? defaultValues.show_notification : false,
      ip: defaultValues ? (defaultValues.ip ? defaultValues.ip : '') : '',
      ibutton1: defaultValues ? (defaultValues.ibutton1 ? defaultValues.ibutton1 : '') : '',
      password: defaultValues ? '' : password,
      two_auth: defaultValues ? (defaultValues.two_auth ? defaultValues.two_auth : 0) : 0,
      time_limit: defaultValues ? (defaultValues.time_limit ? defaultValues.time_limit : '') : '',
    },
  });

  const roleWatch = watch('role_id');
  const isCurrentUserAdmin = session.role_id === config.userRoles.ADMIN;
  const isCurrentUserSuperAdmin = session.role_id === config.userRoles.SUPERADMIN;
  const isEditingAdmin = roleWatch === config.userRoles.ADMIN;
  const isRoleFieldDisabled = isCurrentUserAdmin && isEditingAdmin;
  const isAdminOrSuperAdmin = isCurrentUserAdmin || isCurrentUserSuperAdmin;

  const handleChangeIpInput = (value) => {
    switch (value[value.length - 1]) {
      case ' ':
      case ';':
        switch (value[value.length - 2]) {
          case undefined:
          case ';':
            setValue('ip', value.replace(/.$/, ''), {
              shouldDirty: true,
            });
            break;
          default:
            setValue('ip', value.replace(/.$/, ';'), {
              shouldDirty: true,
            });
        }

        break;
      case ',':
        setValue('ip', value.replace(/.$/, '.'), { shouldDirty: true });
        break;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleChangeTimeInput = (newValue, keyboardInputValue) => {
    if (newValue && newValue.isValid()) {
      setValue('time_limit', newValue.format('YYYY-MM-DD HH:mm:ss'), {
        shouldDirty: true,
      });
    }
  };
  const getDefaultShowNotifications = (id) => {
    switch (id) {
      case 2:
      case 5:
      case 8:
        return true;
      default:
        return false;
    }
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          name: data.name,
          role_id: parseInt(data.role_id),
          email: data.email,
          phone: data.phone,
          can_close_all_notification: !!data.can_close_all_notification,
          show_notification: !!data.show_notification,
          ip: data.ip ? data.ip : undefined,
          ibutton1: data.ibutton1 ? data.ibutton1 : undefined,
          password: data.password ? data.password : undefined,
          time_limit: data.time_limit ? data.time_limit : undefined,
          two_auth: data.two_auth ? Number(data.two_auth) : 0,
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (!defaultValues) {
      setValue('password', password, { shouldDirty: true });
    }
  }, [password]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        role_id: defaultValues.role_id,
        email: defaultValues.email,
        phone: defaultValues.phone,
        ip: defaultValues.ip ? defaultValues.ip : '',
        ibutton1: defaultValues.ibutton1 ? defaultValues.ibutton1 : '',
        can_close_all_notification: defaultValues.can_close_all_notification ? defaultValues.can_close_all_notification : false,
        show_notification: defaultValues.show_notification ? defaultValues.show_notification : false,
        password: '',
        two_auth: defaultValues.two_auth ? defaultValues.two_auth : 0,
        time_limit: defaultValues.time_limit ? defaultValues.time_limit : null,
      });
    }
  }, [defaultValues]);

  useEffect(() => {
    fetchTypeData(!defaultValues);
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.name} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>ФИО</InputLabel>
            <OutlinedInput
              label='ФИО'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Фамилия Имя Отчество'
              {...register('name', {
                pattern: /^([А-Яа-я\s0-9_-]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте в ФИО только буквы кириллицы, цифры и - _</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.role_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Права доступа</InputLabel>
            {isFetchTypeLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchTypeLoading && (
              <Controller
                name='role_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Права доступа'
                    defaultValue=''
                    disabled={(defaultValues && session.id === defaultValues.id) || isRoleFieldDisabled}
                    IconComponent={KeyboardArrowDownRounded}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    notched={firstUpdate}
                    value={field.value}
                    {...register(field.name, {
                      required: true,
                      pattern: /^([0-9]{1,11})?$/iu,
                      onChange: (e) => {
                        const selectedValue = e.target.value;
                        field.value = selectedValue;
                        setValue('show_notification', getDefaultShowNotifications(selectedValue));
                        setValue(field.name, selectedValue);
                      },
                    })}>
                    {typeItems.map((value, key) => (
                      <MenuItem key={key} disableRipple value={value.id}>
                        {value.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
            <FormHelperText>Выберите права доступа пользователя в системе</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.email} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Электронная почта</InputLabel>
            <OutlinedInput
              label='Электронная почта'
              variant='outlined'
              notched={firstUpdate}
              disabled={!!defaultValues}
              fullWidth
              placeholder='username@mail.ru'
              {...register('email', {
                required: true,
                pattern: /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/,
              })}></OutlinedInput>
            <FormHelperText>
              Используйте актуальную электронную почту пользователя, на неё будут приходить системные уведомления.
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.phone} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Номер телефона</InputLabel>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='+7 (999) 999-9999'
                  maskChar=''
                  {...register(field.name, {
                    required: true,

                    pattern: /^((\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d\- ]{7,10}$/im,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Номер телефона'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='+7 (123) 456-7890'
                      onChange={field.onChange}
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте номер телефона пользователя в указанном формате +7 (123) 456-7890.</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      {!defaultValues && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl error={!!formState.errors.password} margin='normal' variant='outlined' fullWidth>
              <InputLabel disableAnimation={firstUpdate}>Пароль</InputLabel>
              <OutlinedInput
                label='Пароль'
                variant='outlined'
                notched={firstUpdate}
                fullWidth
                placeholder='Введите пароль'
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton onClick={generateRandomPassword}>
                      <RestartAltRounded fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                }
                {...register('password', {
                  required: true,
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{6,255}$/,
                })}></OutlinedInput>
              <FormHelperText>
                Используйте сгенерированный пароль или задайте его самостоятельно. Помните, что для входа в приложение пользователю будет
                необходимо указать данный пароль.
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid
          item
          xs={
            roleWatch === config.userRoles['INSPECTOR_CHECKLIST'] || roleWatch === config.userRoles['INSPECTOR_OPERATOR_CHECKLIST'] ? 6 : 12
          }>
          <FormControl error={!!formState.errors.ip} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Разрешенные IP адреса</InputLabel>
            <Controller
              name='ip'
              control={control}
              render={({ field }) => (
                <OutlinedInput
                  label='Разрешенные IP адреса'
                  variant='outlined'
                  notched={firstUpdate}
                  fullWidth
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='192.168.0.1'
                  {...register('ip', {
                    pattern:
                      /^\*$|^(?:\d|1?\d\d|2[0-4]\d|25[0-5])(?:\.(?:\d|1?\d\d|2[0-4]\d|25[0-5])){3}(?:;(?:\d|1?\d\d|2[0-4]\d|25[0-5])(?:\.(?:\d|1?\d\d|2[0-4]\d|25[0-5])){3})*$/iu,
                    onChange: (e) => handleChangeIpInput(e.target.value),
                  })}></OutlinedInput>
              )}></Controller>
            <FormHelperText>
              Используйте IP в соответствие с форматом IPV4. Для указания нескольких IP адресов, используйте формат
              123.124.125.126;123.124.125.127
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.ibutton1} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Ключ идентификатор</InputLabel>
            <Controller
              name='ibutton1'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='********'
                  maskChar=''
                  formatChars={{
                    '*': '([0-9]|[A-Z])',
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^(([0-9]|[A-Z]){8})?$/im,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Ключ идентификатор'
                      variant='outlined'
                      notched={firstUpdate}
                      placeholder='FFAB0000'
                      fullWidth
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>
              Используйте 8-значний ключ доступа к ТС в формате FFAB0000 для идентификации пользователя в приложении проверки чеклистов
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Дата ограничения просмотра данных</InputLabel>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={moment.locale('ru')}>
              <Controller
                name='time_limit'
                control={control}
                error={!!formState.errors.time_limit}
                render={({ field }) => (
                  <DateTimePicker
                    value={field.value}
                    views={['day']}
                    inputFormat='DD.MM.YYYY'
                    disableFuture
                    onChange={handleChangeTimeInput}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <OutlinedInput
                        ref={inputRef}
                        notched={firstUpdate}
                        readOnly
                        label='Дата ограничения просмотра данных'
                        onClick={InputProps.endAdornment?.props.children.props.onClick}
                        inputProps={{
                          ...inputProps,
                          placeholder: 'Выберите дату',
                        }}
                      />
                    )}
                    PaperProps={{
                      sx: (theme) => ({
                        marginTop: '9px',
                        boxShadow: '0px 3px 6px ' + alpha(theme.palette.grey.main, 0.12),
                        '.MuiCalendarOrClockPicker-root > div': {
                          width: '234px',
                          '.MuiCalendarPicker-root': {
                            width: '100%',
                          },
                          '.MuiPickersCalendarHeader-root': {
                            margin: '6px 0',
                          },
                          '.MuiPickersCalendarHeader-labelContainer': {
                            fontSize: '14px',
                            '.MuiPickersCalendarHeader-switchViewButton': {
                              display: 'none',
                            },
                          },
                        },
                        '.MuiPickersArrowSwitcher-root': {
                          padding: '8px 12px',
                          '.MuiButtonBase-root': {
                            width: '26px',
                            height: '26px',
                          },
                        },
                        '.MuiDayPicker-weekDayLabel, .MuiPickersDay-root': {
                          width: '26px',
                          height: '26px',
                        },
                        '.PrivatePickersSlideTransition-root': {
                          minWidth: '234px',
                          minHeight: '182px',
                        },
                        '.MuiDateRangePickerViewDesktop-container:not(:last-of-type)': {
                          borderWidth: '1px',
                        },
                        '.MuiDateRangePickerDay-dayInsideRangeInterval, .MuiPickersDay-today.Mui-selected': {
                          color: theme.palette.primary.contrastText,
                        },
                        '.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                          '&:not(.MuiDateRangePickerDay-rangeIntervalDayHighlightStart):not(.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd)':
                            {
                              borderRadius: 0,
                            },
                          '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                          '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                              borderRadius: '50%',
                            },
                          },
                        },
                        '.MuiDateRangePickerDay-rangeIntervalDayPreview': {
                          borderColor: 'transparent',
                        },
                      }),
                    }}
                  />
                )}></Controller>
            </LocalizationProvider>
            <FormHelperText>
              Выберите дату ограничения просмотра событий. Пользователю будет недоступен диапазон выбора ранее указанной даты.
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      {roleWatch !== config.userRoles['INSPECTOR_CHECKLIST'] && roleWatch !== config.userRoles['INSPECTOR_OPERATOR_CHECKLIST'] && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              label='Включить двухфакторную авторизацию'
              control={
                <Controller
                  name='two_auth'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} onChange={field.onChange} {...register(field.name)} />
                  )}></Controller>
              }
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            opacity: !isCurrentUserSuperAdmin ? 0.5 : 1,
          }}>
          <FormControlLabel
            label='Разрешить закрывать сразу все оповещения об одновременном сливе'
            control={
              <Controller
                name='can_close_all_notification'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    disabled={!isCurrentUserSuperAdmin}
                    size='small'
                    disableRipple
                    checked={!!field.value}
                    onChange={field.onChange}
                    {...register(field.name)}
                  />
                )}></Controller>
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            opacity: !isCurrentUserSuperAdmin ? 0.5 : 1,
          }}>
          <FormControlLabel
            label='Показывать блок с оповещениями об одновременном сливе'
            control={
              <Controller
                name='show_notification'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    disabled={!isAdminOrSuperAdmin}
                    size='small'
                    disableRipple
                    checked={!!field.value}
                    onChange={field.onChange}
                    {...register(field.name)}
                  />
                )}></Controller>
            }
          />
        </Grid>
      </Grid>
    </form>
  );
});

export default UserForm;
